import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Textarea, RadioGroup, Checkbox } from "@/components/ui/form-controls";
import {
  RECOVERY_AFFECTED_WALLET_OPTIONS,
  RECOVERY_REQUEST_DEFAULTS,
  RECOVERY_REQUEST_CUSTODIANS,
} from "@/data/mock";
import type { RecoveryUrgency } from "@/types";

/* RCV-02 — Recovery Request Creation.
   Canonical source: prototype/pages/recovery/request.html (no current
   Figma frame located). The canonical 3-step wizard header only wires
   Steps 1↔2 in its own JS (`goStep2()`/`goStep1()`) — "Step 3 — Review
   & Submit" is a PERMANENTLY greyed/disabled stub the source's own
   script never unlocks, and "Submit for Approval →" on Step 2 navigates
   directly to approval.html, skipping Step 3 entirely. That exact
   limitation is reproduced faithfully below — Step 3 is rendered as a
   non-interactive disabled tab, never a working review screen.

   The canonical "Affected Wallet" options (WLT-ILL-001..004) are
   retired illustrative wallet ids per this engagement's Treasury Data
   Foundation convention; substituted here with the real MOCK_WALLETS
   universe (see RECOVERY_AFFECTED_WALLET_OPTIONS in data/mock.ts). */

const RECOVERY_TYPES = ["Test Only", "Full Key Recovery", "Shard Replacement", "Emergency Access"];

const URGENCY_OPTIONS: { value: RecoveryUrgency; label: string }[] = [
  { value: "normal", label: "Normal" },
  { value: "elevated", label: "Elevated" },
  { value: "emergency", label: "Emergency" },
];

export function RecoveryRequest() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2>(1);

  const [recoveryType, setRecoveryType] = useState(RECOVERY_REQUEST_DEFAULTS.recoveryType);
  const [reason, setReason] = useState(RECOVERY_REQUEST_DEFAULTS.reason);
  const [affectedWallet, setAffectedWallet] = useState(RECOVERY_REQUEST_DEFAULTS.affectedWallet);
  const [urgency, setUrgency] = useState<RecoveryUrgency>(RECOVERY_REQUEST_DEFAULTS.urgency);
  const [confirmed, setConfirmed] = useState(false);

  const [notified, setNotified] = useState<Record<string, boolean>>(
    Object.fromEntries(RECOVERY_REQUEST_CUSTODIANS.map((c) => [c.name, c.required]))
  );
  const [notifyMethod, setNotifyMethod] = useState("in-app");

  const requesterName = "Daniel Park";

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-extrabold text-ink">Initiate Recovery Request</h1>
        <p className="text-sm text-muted">Submit a new key recovery request for custodian approval.</p>
      </div>

      <div className="mb-6 flex gap-1.5">
        <StepTab label="1. Request Details" active={step === 1} onClick={() => setStep(1)} />
        <StepTab label="2. Notify Custodians" active={step === 2} onClick={() => setStep(2)} />
        <StepTab label="3. Review & Submit" active={false} disabled />
      </div>

      {step === 1 && (
        <div className="max-w-xl rounded-[10px] border border-line bg-surface p-6">
          <div className="space-y-4">
            <Field label="Recovery Type">
              <Select value={recoveryType} onChange={(e) => setRecoveryType(e.target.value)} className="w-full">
                {RECOVERY_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </Select>
            </Field>

            <Field label="Reason">
              <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="w-full" />
            </Field>

            <Field label="Affected Wallet">
              <Select value={affectedWallet} onChange={(e) => setAffectedWallet(e.target.value)} className="w-full">
                {RECOVERY_AFFECTED_WALLET_OPTIONS.map((w) => (
                  <option key={w} value={w}>{w}</option>
                ))}
              </Select>
            </Field>

            <Field label="Urgency">
              <RadioGroup
                ariaLabel="Urgency"
                value={urgency}
                onChange={(v) => setUrgency(v as RecoveryUrgency)}
                options={URGENCY_OPTIONS}
              />
            </Field>

            <label className="flex items-start gap-2.5 text-sm text-ink">
              <Checkbox checked={confirmed} onCheckedChange={setConfirmed} ariaLabel="I confirm this recovery request is authorized" />
              I confirm this recovery request is authorized and accurate.
            </label>
          </div>

          <Button className="mt-5" disabled={!confirmed} onClick={() => setStep(2)}>Continue →</Button>
        </div>
      )}

      {step === 2 && (
        <div className="max-w-xl rounded-[10px] border border-line bg-surface p-6">
          <div className="mb-4 text-sm font-bold text-ink">Notify Custodians</div>
          <div className="mb-5 space-y-2.5">
            {RECOVERY_REQUEST_CUSTODIANS.map((c) => (
              <label key={c.name} className="flex items-center gap-2.5 text-sm text-ink">
                <Checkbox
                  checked={notified[c.name]}
                  onCheckedChange={(v) => setNotified((s) => ({ ...s, [c.name]: v }))}
                  ariaLabel={c.name}
                  disabled={c.required}
                />
                <span>
                  {c.name} <span className="text-xs text-muted">— {c.sub}</span>
                </span>
              </label>
            ))}
          </div>

          <Field label="Notification Method">
            <RadioGroup
              ariaLabel="Notification Method"
              value={notifyMethod}
              onChange={setNotifyMethod}
              options={[
                { value: "in-app", label: "In-app" },
                { value: "email", label: "Email" },
                { value: "both", label: "Both" },
              ]}
            />
          </Field>

          <div className="mt-4 rounded-md border border-line bg-bg p-3 text-xs italic text-muted">
            {RECOVERY_REQUEST_DEFAULTS.messagePreview(requesterName, recoveryType, reason)}
          </div>

          <div className="mt-5 flex gap-2.5">
            <Button variant="secondary" onClick={() => setStep(1)}>← Back</Button>
            <Button onClick={() => navigate("/recovery/approval")}>Submit for Approval →</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function StepTab({ label, active, disabled, onClick }: { label: string; active: boolean; disabled?: boolean; onClick?: () => void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-current={active ? "step" : undefined}
      className={`rounded-[6px] border px-3 py-1.5 text-xs font-semibold ${
        active
          ? "border-primary bg-primary text-on-primary"
          : disabled
            ? "cursor-not-allowed border-line bg-disabled-soft text-disabled"
            : "border-line bg-surface text-muted"
      }`}
    >
      {label}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted">{label}</label>
      {children}
    </div>
  );
}
